import React, {useEffect, useState, useRef} from "react";
import {useFetchHook} from "../../hooks/useFetchHook";
import fetcher from "../../fetcher";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import _ from 'lodash'
import {RebatesTableRow} from "./RebatesTableRow";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "react-bootstrap/Button";
import {useModalsStore} from "../../stores/modalsStore";
import Stack from "@mui/material/Stack";
import {LEASE_DEAL_DARK} from "../../contexts/DealContext";
import {Paper, TableContainer} from "@mui/material";
import Collapse from "@mui/material/Collapse";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import IconButton from "@mui/material/IconButton";
import {useDeskingContext} from "../../contexts/DeskingContext";
import {useMediaQuery} from "react-responsive";
import {mediaQueryForMobile} from "../HomePage/HomePage";

export const RebatesTable = (props) => {
    const {
        applySelectedRebates,
        option_code_ids,
        quotes,
    } = props

    const isTabletOrMobile = useMediaQuery({ query: mediaQueryForMobile })

    const [rebatesOpen, setRebatesOpen] = useState(false)
    const scrollRef = useRef(null)

    const [rebates, setRebates] = useState([])
    const [compatibiltyData, setCompatibilityData] = useState(null)

    const initialAppliedRebatedIds = quotes.flatMap(q => q.applied_rebate_ids)

    const [checkedIds, setCheckedIds] = useState([...new Set(initialAppliedRebatedIds)])

    const [fetchRebatesResponse, fetchRebates] = useFetchHook(fetcher.getRebates)
    const [fetchCompatibilitiesResponse, fetchCompatibilities] = useFetchHook(fetcher.getRebateCompatibilities)

    const { fetchDeskingResponse } = useDeskingContext()

    const closeModal = useModalsStore((state) => state.closeModal)

    const toggleRebate = (rebateId) => {
        const newChecked = checkedIds.includes(rebateId) ? checkedIds.filter(externalId => externalId !== rebateId) : [...checkedIds, rebateId]
        setCheckedIds(newChecked)
    }

    const isChecked = (rebateId) => {
        return checkedIds.includes(rebateId)
    }

    const isApplied = (rebateId, leadingRebateId) => {
        if (compatibiltyData && isChecked(rebateId)) {
            const compatList = compatibiltyData[leadingRebateId] || []
            return compatList.includes(rebateId)
        }
        return false
    }

    const sumTotalCash = (leadingRebateId, dealerOrCustomer) => {
        let total = 0
        rebates.forEach(r => {
            if (isApplied(r.external_id, leadingRebateId) && r.recipient === dealerOrCustomer) {
                total += Number(r.amount)
            }
        })
        return total
    }

    const selectedRebates = () => {
        const selected = []
        // if rebates is in any applied array its applied
        if (compatibiltyData) {
            rebates.forEach(r => {
                if (isChecked(r.external_id)) {
                    selected.push(r)
                }
            })
        }
        return selected
    }

    useEffect(function fetchRebatesOnMount(){
        fetchRebates({
            ...props,
            option_code_ids,
        })
    }, [])

    useEffect(function setFetchedRebates(){
        if (fetchRebatesResponse?.data?.data) {
            setRebates(fetchRebatesResponse?.data?.data)
        }
    }, [fetchRebatesResponse])


    useEffect(function setRebateCompatibilities(){
        if (fetchCompatibilitiesResponse?.data?.data) {
            if (!_.isEmpty(fetchCompatibilitiesResponse?.data?.data)) {
                setCompatibilityData(fetchCompatibilitiesResponse?.data?.data)
            } else {
                setCompatibilityData(null)
            }
        }
    }, [fetchCompatibilitiesResponse])

    useEffect(function fetchCompatibitliesOnChange(){
        fetchCompatibilities({
            ...props,
            rebate_ids: checkedIds,
            option_code_ids: option_code_ids
        })
    }, [checkedIds])

    // const nonManualRebates = rebates.filter(r => !r.is_manual)
    const leadingRebates = rebates.filter(r => r.compatible_rebate_ids.length !== 0)
    const regularRebates = rebates.filter(r => r.compatible_rebate_ids.length === 0)
    const leadingRebatesWithTerms = leadingRebates.filter(r => r.terms.length !== 0)
    const allTerms = leadingRebatesWithTerms.flatMap(r => r.terms).map(r => r.term)

    const isLoading = fetchDeskingResponse?.loading

    return (
        <Paper
            ref={scrollRef}
            className={'RebatesTable'}
            sx={{borderRadius: '8px', padding: '10px', marginTop: '20px', background: 'var(--rb-paper-bg, #ffffff)', color: 'var(--rb-text, #374151)'}}
        >
            <Stack
                direction={'row'}
                justifyContent="space-between"
                alignItems="center"
                className={'rebates-header'}
                onClick={() => setRebatesOpen(!rebatesOpen)}
                sx={{ cursor: 'pointer' }}
            >
                <Typography
                    variant="p"
                    sx={{
                        display: "flex",
                        alignItems: "center",
                    }}
                >
                    Vehicle Rebates and Incentives
                </Typography>
                {/* Indicator only — the whole bar above is the click target. */}
                <IconButton aria-label="expand row" disableRipple>
                    {rebatesOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </IconButton>
            </Stack>

            {
                Boolean(rebates.length) &&
                <Collapse
                    in={rebatesOpen}
                    timeout="auto"
                    onEntered={() => {
                        if (scrollRef.current) {
                            scrollRef.current.scrollIntoView({
                                behavior: 'smooth',
                                block: 'end'
                            });
                        }
                    }}
                >
                    <TableContainer sx={{ maxHeight: 600, overflow: 'auto', opacity: isLoading ? .5 : 1 }}>
                        <Table
                            size={'small'}
                            stickyHeader
                        >
                            <TableHead className={"rebatesTable-header"}>
                                <TableRow>
                                    <TableCell className={'rebatesTable-header__cell'}>
                                        <div style={{height: "24px"}}></div>
                                        {allTerms.map(t => <div key={t} style={{height: "24px", background: "var(--rb-alt-bg, #F6F6F6)"}}></div>)}
                                        <div style={{height: "24px", background: "var(--rb-total-bg, #ecfdf5)"}}></div>
                                        <div style={{height: "24px", background: "var(--rb-total-bg, #ecfdf5)"}}></div>
                                    </TableCell>
                                    <TableCell
                                        className={'rebatesTable-header__cell'}
                                        style={{color: "var(--rb-text, #374151)"}}
                                    >
                                        <Stack
                                            flexDirection={'column'}
                                        >
                                            <span
                                                className={'header-cell'}
                                                style={{color: "var(--rb-text, #374151)"}}
                                            >
                                                Rebate Program
                                            </span>
                                            {
                                                allTerms.map(t => (
                                                    <span
                                                        key={t}
                                                        className={'header-cell'}
                                                        style={{color: "var(--rb-text, #374151)", background: "var(--rb-alt-bg, #F6F6F6)"}}
                                                    >
                                                        {isTabletOrMobile ? t : `Term: ${t} months`}
                                            </span>
                                                ))
                                            }
                                            <span
                                                className={'header-cell'}
                                                style={{color: "var(--rb-total-text, #059669)", background: "var(--rb-total-bg, #ecfdf5)"}}
                                            >
                                                Total Other Cash
                                            </span>
                                            <span
                                                className={'header-cell'}
                                                style={{color: "var(--rb-total-text, #059669)", background: "var(--rb-total-bg, #ecfdf5)"}}
                                            >
                                                Total Dealer Cash
                                            </span>
                                        </Stack>
                                    </TableCell>
                                    <TableCell className={'rebatesTable-header__cell'}>
                                        <div style={{ height: "24px"}}></div>
                                        {allTerms.map(t => <div key={t} style={{height: "24px", background: "var(--rb-alt-bg, #F6F6F6)"}}></div>)}
                                        <div style={{ height: "24px", background: "var(--rb-total-bg, #ecfdf5)"}}></div>
                                        <div style={{ height: "24px", background: "var(--rb-total-bg, #ecfdf5)"}}></div>
                                    </TableCell>
                                    {
                                        leadingRebates.map(leadingRebate => (
                                            <TableCell
                                                key={leadingRebate.external_id}
                                                className={'rebatesTable-header__cell'}
                                                style={{color: "var(--rb-text, #374151)"}}
                                            >
                                                <Stack
                                                    flexDirection={'column'}
                                                    // justifyContent={'end'}
                                                    alignItems={'center'}
                                                >
                                                    <Box style={{whiteSpace: 'nowrap'}}>
                                                        {leadingRebate.name}
                                                    </Box>
                                                    {
                                                        allTerms.map(term => {
                                                            const amount = leadingRebate.terms.find(t => t.term === term)?.amount
                                                            return (
                                                                <Box
                                                                    key={term}
                                                                    style={{
                                                                        height: '24px',
                                                                        background: "var(--rb-alt-bg, #F6F6F6)",
                                                                        width: '100%',
                                                                        textAlign: 'center',
                                                                }}
                                                                >
                                                                    {amount && amount.toDollarFormat()}
                                                                </Box>
                                                            )
                                                        })
                                                    }
                                                    <Box
                                                        style={{color: "var(--rb-total-text, #059669)", background: "var(--rb-total-bg, #ecfdf5)", width: '100%', textAlign: 'center'}}
                                                        className={'header-cell'}
                                                    >
                                                        {sumTotalCash(leadingRebate.external_id, 'Customer').toDollarFormat()}
                                                    </Box>
                                                    <Box
                                                        style={{color: "var(--rb-total-text, #059669)", background: "var(--rb-total-bg, #ecfdf5)", width: '100%', textAlign: 'center'}}
                                                        className={'header-cell'}
                                                    >
                                                        {sumTotalCash(leadingRebate.external_id, 'Dealer').toDollarFormat()}
                                                    </Box>
                                                </Stack>
                                            </TableCell>
                                        ))
                                    }
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {
                                    regularRebates.map((rebate, idx) => (
                                        <RebatesTableRow
                                            key={rebate.external_id}
                                            rebate={rebate}
                                            leadingRebates={leadingRebates}
                                            isApplied={isApplied}
                                            toggleRebate={toggleRebate}
                                            isChecked={isChecked(rebate.external_id)}
                                            isLoading={fetchCompatibilitiesResponse?.loading}
                                        />
                                    ))
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Stack
                        flexDirection={'row'}
                        justifyContent={'end'}
                        style={{ paddingTop: '20px'}}
                    >
                        <Button
                            style={{
                                background: "var(--rb-cancel-bg, #f3f4f6)",
                                color: "var(--rb-text, #374151)",
                                border: 'none',
                                fontSize: '14px'
                            }}
                            className={'button'}
                            onClick={() => closeModal("RebatesModal")}
                            disabled={fetchCompatibilitiesResponse?.loading}
                        >
                            Cancel
                        </Button>
                        <Button
                            className={'button'}
                            style={{
                                background: LEASE_DEAL_DARK,
                                marginLeft: '10px',
                                fontSize: '14px'
                            }}
                            onClick={() => applySelectedRebates(selectedRebates(), option_code_ids)}
                            disabled={fetchCompatibilitiesResponse?.loading}
                        >
                            Apply Selected Rebates
                        </Button>
                    </Stack>
                </Collapse>
            }
        </Paper>
    )
}